import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { MainContext } from "../context/MainContext";
import { isUserCompletedProfile } from "../utils";
import fetchService from "../utils/fetch.service";

export const FormStyled = styled.section`
    width: 100%;
    padding: 4px 0 0;

    @media (min-width: 768px) {
        padding: 32px;
        background: rgba(17, 27, 43, 0.86);
        border: 1px solid var(--line-strong);
        border-radius: var(--radius-lg);
        box-shadow: 0 18px 55px rgba(0, 0, 0, 0.2);
    }
`;

export const FormItemHeadingStyled = styled.h2`
    margin: 0 0 14px;
    color: var(--text);
    font-size: 16px;
    font-weight: 700;
`;

const Form = styled.div`
    display: grid;
    gap: 24px;

    @media (min-width: 900px) {
        grid-template-columns: 1fr 1.25fr;
        gap: 28px 36px;

        .wide { grid-column: 1 / -1; }
    }

    @media (max-width: 480px) {
        gap: 18px;
    }
`;

const ChoiceGroup = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
`;

const Choice = styled.label`
    min-height: 56px;
    padding: 0 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    border: 1px solid ${ props => props.$selected ? "var(--accent)" : "var(--line-strong)" };
    border-radius: 13px;
    background: ${ props => props.$selected ? "var(--accent-soft)" : "rgba(8, 15, 27, 0.35)" };
    color: ${ props => props.$selected ? "#ff9fc2" : "var(--text-soft)" };
    font-weight: 700;
    cursor: pointer;

    input {
        width: 20px;
        height: 20px;
        margin: 0;
        accent-color: var(--accent);
    }
`;

const RangeHeader = styled.div`
    margin-bottom: 12px;
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 16px;

    h2 { margin: 0; }
    span { color: var(--text-soft); font-size: 14px; }
`;

const RangeWrap = styled.div`
    position: relative;
    height: 42px;
    margin: 2px 9px 0;

    .track,
    .highlight {
        position: absolute;
        top: 19px;
        right: 0;
        left: 0;
        height: 5px;
        border-radius: 999px;
        background: #344055;
    }

    .highlight {
        right: ${ props => 100 - props.$right }%;
        left: ${ props => props.$left }%;
        background: var(--accent);
    }

    input[type="range"] {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 42px;
        margin: 0;
        appearance: none;
        background: transparent;
        pointer-events: none;
    }

    input[type="range"]::-webkit-slider-thumb {
        width: 24px;
        height: 24px;
        appearance: none;
        border: 3px solid var(--accent);
        border-radius: 50%;
        background: var(--text);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
        cursor: grab;
        pointer-events: auto;
    }

    input[type="range"]::-moz-range-thumb {
        width: 20px;
        height: 20px;
        border: 3px solid var(--accent);
        border-radius: 50%;
        background: var(--text);
        cursor: grab;
        pointer-events: auto;
    }
`;

const RangeMeta = styled.div`
    display: flex;
    justify-content: space-between;
    color: var(--muted);
    font-size: 13px;
`;

const NumberFields = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-top: 14px;

    label {
        color: var(--muted);
        font-size: 12px;
        font-weight: 650;
    }

    input {
        width: 100%;
        min-height: 44px;
        margin-top: 5px;
        padding: 8px 12px;
        border: 1px solid var(--line-strong);
        border-radius: var(--radius-sm);
        background: rgba(8, 15, 27, 0.5);
        color: var(--text);
    }

    @media (max-width: 767px) {
        display: none;
    }
`;

const VerifiedRow = styled.div`
    min-height: 74px;
    padding: 14px;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    border-top: 1px solid var(--line);
    border-bottom: 1px solid var(--line);

    input {
        width: 22px;
        height: 22px;
        margin: 2px 0 0;
        flex: 0 0 auto;
        accent-color: var(--accent);
    }

    label {
        color: var(--text);
        font-weight: 700;
    }

    p {
        margin: 3px 0 0;
        color: var(--muted);
        font-size: 13px;
    }
`;

const SearchButton = styled.button`
    width: 100%;
    min-height: 56px;
    border-radius: 13px;
    background: var(--accent);
    color: #24101a;
    font-weight: 800;
    cursor: pointer;

    &:hover:not(:disabled) { background: var(--accent-strong); }
    &:disabled { cursor: not-allowed; opacity: 0.45; }
`;

const ErrorMessage = styled.p`
    margin: -10px 0 0;
    color: #fda4af;
    font-size: 14px;
`;

const DisabledHelp = styled.div`
    margin-bottom: 10px;
    padding: 10px 12px;
    border-left: 3px solid var(--warning);
    border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
    background: rgba(240, 194, 123, 0.07);
    color: var(--text-soft);
    font-size: 13px;
    line-height: 1.4;

    strong {
        display: block;
        margin-bottom: 2px;
        color: var(--text);
    }

    a {
        margin-left: 4px;
        font-weight: 750;
        text-decoration: underline;
        text-underline-offset: 2px;
    }

    @media (min-width: 620px) {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 16px;

        strong {
            display: inline;
            margin-bottom: 0;
        }
    }
`;

const clampAge = (value, min, max) => Math.max(min, Math.min(Number(value) || min, max));

const SearchPanel = () => {
    const { userContext, profilesContext, searchedContext, isSearchingContext } = useContext(MainContext);
    const [user] = userContext;
    const [, setProfiles] = profilesContext;
    const [, setSearched] = searchedContext;
    const [, setIsSearching] = isSearchingContext;
    const [lookFor, setLookFor] = useState(localStorage.getItem("lookFor") || "woman");
    const [ages, setAges] = useState(() => {
        const saved = localStorage.getItem("ages");
        return saved ? JSON.parse(saved) : [22, 50];
    });
    const [verified, setVerified] = useState(false);
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState("");
    const isUserVerified = Boolean(user.po);

    const updateLookFor = (value) => {
        localStorage.setItem("lookFor", value);
        setLookFor(value);
    };

    const updateAges = (next) => {
        localStorage.setItem("ages", JSON.stringify(next));
        setAges(next);
    };

    const search = async () => {
        if (searching || !isUserCompletedProfile(user)) return;
        setSearching(true);
        setIsSearching(true);
        setError("");
        try {
            const response = await fetchService.post("/user-s", { lookFor, ages, verified });
            if (!response || !Array.isArray(response.data)) throw new Error("Search failed");
            setProfiles(response.data);
            setSearched(true);
        } catch (searchError) {
            setProfiles([]);
            setSearched(false);
            setError("We could not load matches. Please try again.");
        } finally {
            setSearching(false);
            setIsSearching(false);
        }
    };

    const leftPercent = ((ages[0] - 18) / 82) * 100;
    const rightPercent = ((ages[1] - 18) / 82) * 100;

    return (
        <FormStyled aria-labelledby="search-heading">
            <Form>
                <div>
                    <FormItemHeadingStyled id="search-heading">I’m looking for</FormItemHeadingStyled>
                    <ChoiceGroup role="radiogroup" aria-label="Who are you looking for?">
                        { ["woman", "man"].map((value) => (
                            <Choice key={ value } $selected={ lookFor === value }>
                                <input
                                    type="radio"
                                    name="lookfor"
                                    value={ value }
                                    checked={ lookFor === value }
                                    onChange={ () => updateLookFor(value) }
                                />
                                { value === "woman" ? "Women" : "Men" }
                            </Choice>
                        )) }
                    </ChoiceGroup>
                </div>

                <div>
                    <RangeHeader>
                        <FormItemHeadingStyled>Age range</FormItemHeadingStyled>
                        <span>Ages { ages[0] }–{ ages[1] }</span>
                    </RangeHeader>
                    <RangeWrap $left={ leftPercent } $right={ rightPercent }>
                        <div className="track"/>
                        <div className="highlight"/>
                        <label className="sr-only" htmlFor="min-age-range">Minimum age</label>
                        <input
                            id="min-age-range"
                            type="range"
                            min="18"
                            max="100"
                            value={ ages[0] }
                            onChange={ event => updateAges([Math.min(Number(event.target.value), ages[1] - 1), ages[1]]) }
                        />
                        <label className="sr-only" htmlFor="max-age-range">Maximum age</label>
                        <input
                            id="max-age-range"
                            type="range"
                            min="18"
                            max="100"
                            value={ ages[1] }
                            onChange={ event => updateAges([ages[0], Math.max(Number(event.target.value), ages[0] + 1)]) }
                        />
                    </RangeWrap>
                    <RangeMeta><span>18</span><span>100</span></RangeMeta>
                    <NumberFields>
                        <label>
                            Minimum age
                            <input
                                type="number"
                                min="18"
                                max={ ages[1] - 1 }
                                value={ ages[0] }
                                onChange={ event => updateAges([clampAge(event.target.value, 18, ages[1] - 1), ages[1]]) }
                            />
                        </label>
                        <label>
                            Maximum age
                            <input
                                type="number"
                                min={ ages[0] + 1 }
                                max="100"
                                value={ ages[1] }
                                onChange={ event => updateAges([ages[0], clampAge(event.target.value, ages[0] + 1, 100)]) }
                            />
                        </label>
                    </NumberFields>
                </div>

                <VerifiedRow className="wide">
                    <input
                        id="verified-only"
                        type="checkbox"
                        disabled={ !isUserVerified }
                        checked={ verified }
                        onChange={ () => setVerified((value) => !value) }
                    />
                    <div>
                        <label htmlFor="verified-only">Verified only { !isUserVerified && "· Locked" }</label>
                        { !isUserVerified && <p><Link to="/verify">Verify to unlock</Link> this filter.</p> }
                    </div>
                </VerifiedRow>

                <div className="wide">
                    { !isUserCompletedProfile(user) && (
                        <DisabledHelp role="note">
                            <strong>Find people is unavailable.</strong>
                            <span>Complete your profile to enable search.<Link to="/profile">Finish profile</Link></span>
                        </DisabledHelp>
                    ) }
                    <SearchButton
                        type="button"
                        disabled={ searching || !isUserCompletedProfile(user) }
                        onClick={ search }
                    >
                        { searching ? "Finding people…" : "Find people" }
                    </SearchButton>
                </div>
                { error && <ErrorMessage className="wide" role="alert">{ error }</ErrorMessage> }
            </Form>
        </FormStyled>
    );
};

export default SearchPanel;

export const SEX_OPTIONS = [
    { value: "straight", text: "Straight", klo: ["man", "woman"] },
    { value: "gay", text: "Gay", klo: ["man"] },
    { value: "lesbian", text: "Lesbian", klo: ["woman"] }
];
